<template>
    <div
        v-if="type === 'dots'"
        class="dots flex items-center"
        :class="[{ left: 'justify-start', center: 'justify-center', right: 'justify-end'}[align], fullWidth ? 'w-full' : '']"
    >
        <div class="sphere1 bg-primary" :class="[classes.size, classes.margin]"></div>
        <div class="sphere2 bg-primary" :class="[classes.size, classes.margin]"></div>
        <div class="sphere3 bg-primary" :class="[classes.size, classes.margin]"></div>
    </div>
    <div
        v-else-if="type === 'bar'"
        class="bar flex items-center"
        :class="[{ left: 'justify-start', center: 'justify-center', right: 'justify-end'}[align], fullWidth ? 'w-full' : '']"
    >
        <div class="relative bg-primary-5 rounded w-full overflow-hidden" :class="[classes.size, classes.margin]">
            <div class="absolute active bg-primary rounded h-full"></div>
        </div>
    </div>
    <div
        v-else-if="type === 'spinner'"
        class="spinner flex items-center gap-8"
        :class="[{ left: 'justify-start', center: 'justify-center', right: 'justify-end'}[align], fullWidth ? 'w-full' : '']"
    >
        <svg-icon
            v-if="!done"
            :class="[classes.size, classes.margin]"
            type="spinner"
        />
        <svg-icon
            v-else
            class="fill-secondary finished"
            :class="[classes.size, classes.margin]"
            type="check-circle-filled"
        />
        {{ text }}
    </div>
    <div v-else-if="type === 'progress'" class="outer-circle rounded-full" :class="classes.size" style="--progress: 0%;">
        <div class="inner-circle group-hover:bg-primary absolute rounded-full bg-white" :class="progressInnerClass[size]"></div>
    </div>
</template>

<script>

import SvgIcon from './SvgIcon.vue';
export default {
    components: {
        'svg-icon': SvgIcon,
    },
    props: {
        color: {
            type: String,
            default: 'grey-darkest',
        },
        size: {
            type: String,
            default: 'large',
            validator: value => ['small', 'medium', 'large'].includes(value),
        },
        align: {
            type: String,
            default: 'center',
            validator: value => ['left', 'center', 'right'].includes(value),
        },
        type: {
            type: String,
            default: 'dots',
            validator: value => ['dots', 'bar', 'spinner', 'progress'].includes(value),
        },
        text: String,
        done: Boolean,
        fullWidth: Boolean,
        progress: Number,
    },
    data() {
        return {
            dotsClasses: {
                small: { size: 'w-8 h-8', margin: 'mr-4'},
                medium: { size: 'w-16 h-16', margin: 'mr-4'},
                large: { size: 'w-24 h-24', margin: 'mr-8'},
            },
            barClasses: {
                small: { size: 'h-2', margin: ''},
                medium: { size: 'h-4', margin: ''},
                large: { size: 'h-6', margin: ''},
            },
            spinnerClasses: {
                small: { size: 'w-16 h-16', margin: ''},
                medium: { size: 'w-32 h-32', margin: ''},
                large: { size: 'w-48 h-48', margin: ''},
            },
            progressClasses: {
                small: { size: 'w-16 h-16', margin: ''},
                medium: { size: 'w-32 h-32', margin: ''},
                large: { size: 'w-48 h-48', margin: ''},
            },
            progressInnerClass: {
                small: 'w-12 h-12',
                medium: 'w-28 h-28',
                large: 'w-40 h-40',
            }
        }
    },
    computed: {
        classes () {
            return this[this.type + 'Classes'][this.size];
        },
        outerCircle() {
            return document.querySelector('.outer-circle');
        },
    },
    watch: {
        progress() {
            if (this.progress === 0) {
                this.outerCircle.style.background = `conic-gradient(#ddd 0deg, #ddd 0deg)`;
                return;
            }

            const angle = (this.progress / 100) * 360;
            this.outerCircle.style.background = `conic-gradient(#4caf50 ${angle}deg, #ddd ${angle}deg)`;
        }
    }
}
</script>


<style scoped>
.dots {
    & > div {
        @apply rounded-32;
        animation: loading-dots 1.4s infinite ease-in-out both;
    }
    & .sphere1 {
        animation-delay: -0.32s;
    }
    & .sphere2 {
        animation-delay: -0.16s;
    }
}

.bar {
    & > div > .active {
        animation: loading-bar 2s infinite ease-in-out both;
    }
}

.spinner {
    & > svg:not(.finished) {
        animation: rotate 1s linear infinite;
    }
}

.progress {
    & > svg:not(.finished) {
        animation: progress 1s linear infinite;
    }
    & > .progress-bar > .active {
        animation: loading-bar 2s infinite ease-in-out both;
    }
}

@keyframes progress {
    0% {
        width: 0;
    }
    100% {
        width: 100%;
    }
}

@keyframes loading-dots {
    0%, 80%, 100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1.0);
    }
}

@keyframes loading-bar {
    0% {
        @apply w-1/3 start-0 -translate-x-1/2;
    }
    50% {
        @apply w-1/3 start-full -translate-x-1/2
    }
    100% {
        @apply w-1/3 start-0 -translate-x-1/2;
    }
}

@keyframes rotate {
    100% {
        transform: rotate(-360deg);
    }
}

.outer-circle {
    background: conic-gradient(#ddd 0deg, #ddd 360deg);
    display: flex;
    justify-content: center;
    align-items: center;
}

.progress-text {
    font-size: 1.5em;
    font-weight: bold;
}
</style>
